import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Weight, Tags, DownloadCloud } from 'lucide-react';
import api from '@/services/apiClient';
import PageHeader from '@/components/ui/PageHeader';
import DataTable from '@/components/ui/DataTable';
import Button from '@/components/ui/Button';
import Select from '@/components/ui/Select';
import Input from '@/components/ui/Input';
import Toolbar from '@/components/ui/Toolbar';
import Modal from '@/components/ui/Modal';
import EmptyState from '@/components/ui/EmptyState';
import EquipmentForm from './EquipmentForm';
import CategoriesManager from './CategoriesManager';

/**
 * Cadastro de equipamentos. `mode`:
 *  - 'global' (admin): cria/edita equipamentos globais, scope=global
 *  - 'library' (trainer): vê todos (scope=all), cria os próprios
 */
export default function Equipments({ mode = 'global' }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isAdmin = mode === 'global';
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [categoryId, setCategoryId] = useState('');
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // {} novo | obj editar | null fechado
  const [managingCats, setManagingCats] = useState(false);

  const loadCategories = () => {
    api
      .get('/equipment-categories')
      .then((res) => setCategories((res.data || res).categories || res.data || []))
      .catch(() => setCategories([]));
  };

  useEffect(loadCategories, []);

  const load = () => {
    setLoading(true);
    const params = { scope: isAdmin ? 'global' : 'all' };
    if (categoryId) params.categoryId = categoryId;
    if (q) params.q = q;
    api
      .get('/equipments', params)
      .then((res) => setRows((res.data || res).equipments || res.data || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const id = setTimeout(load, 300);
    return () => clearTimeout(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryId, q]);

  const save = async (form) => {
    if (editing?.id) await api.put(`/equipments/${editing.id}`, form);
    else await api.post('/equipments', form);
    setEditing(null);
    load();
  };

  const remove = async (id) => {
    if (!confirm(t('equipments.confirmDelete'))) return;
    try {
      await api.delete(`/equipments/${id}`);
      load();
    } catch (e) {
      alert(e.message);
    }
  };

  const thumbStriped = {
    width: 44,
    height: 34,
    borderRadius: 6,
    flex: 'none',
    background:
      'repeating-linear-gradient(45deg, var(--surf2), var(--surf2) 5px, color-mix(in srgb, var(--surf2) 60%, var(--bg)) 5px, color-mix(in srgb, var(--surf2) 60%, var(--bg)) 10px)',
  };

  const columns = [
    {
      key: 'name',
      header: t('equipments.colEquipment'),
      render: (r) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {r.imageUrl ? (
            <img src={r.imageUrl} alt="" style={{ width: 44, height: 34, borderRadius: 6, objectFit: 'cover', flex: 'none' }} />
          ) : (
            <span style={thumbStriped} aria-hidden />
          )}
          <span
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 600,
              fontSize: 15,
              textTransform: 'uppercase',
              color: 'var(--hi)',
            }}
          >
            {r.name}
          </span>
        </div>
      ),
    },
    { key: 'category', header: t('equipments.colCategory'), render: (r) => <span style={{ color: 'var(--mid)', fontSize: 13 }}>{r.category?.name || '—'}</span> },
    { key: 'brand', header: t('equipments.colBrand'), render: (r) => <span style={{ color: 'var(--mid)', fontSize: 13 }}>{r.brand || '—'}</span> },
    {
      key: 'actions',
      header: t('screens.actions'),
      align: 'right',
      render: (r) => {
        // trainer só edita os próprios; admin edita tudo
        const canEdit = isAdmin || !r.isGlobal;
        if (!canEdit) return <span style={{ color: 'var(--low)', fontSize: 11 }}>{t('equipments.global')}</span>;
        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <Button size="sm" variant="ghost" icon={Pencil} onClick={() => setEditing(r)}>{t('common.edit')}</Button>
            <Button size="sm" variant="ghost" icon={Trash2} onClick={() => remove(r.id)} />
          </div>
        );
      },
    },
  ];

  return (
    <>
      <PageHeader kicker={isAdmin ? 'Movez · Admin' : 'Movez · Personal'} title={t('nav.equipments')}>
        {isAdmin && (
          <>
            <Button variant="outline" icon={DownloadCloud} onClick={() => navigate('/admin/equipments/import')}>
              {t('equipments.importButton')}
            </Button>
            <Button variant="outline" icon={Tags} onClick={() => setManagingCats(true)}>{t('equipments.manageCategories')}</Button>
          </>
        )}
        <Button icon={Plus} onClick={() => setEditing({})}>{t('screens.newEquipment')}</Button>
      </PageHeader>

      <Toolbar>
        <Input placeholder={t('common.search')} value={q} onChange={(e) => setQ(e.target.value)} style={{ minWidth: 240 }} />
        <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}
          options={[{ value: '', label: t('equipments.allCategories') }, ...categories.map((c) => ({ value: String(c.id), label: c.name }))]} />
      </Toolbar>

      <DataTable
        columns={columns}
        rows={rows}
        loading={loading}
        empty={<EmptyState icon={Weight} title={t('equipments.emptyTitle')} description={t('equipments.emptyDesc')} action={<Button icon={Plus} onClick={() => setEditing({})}>{t('screens.newEquipment')}</Button>} />}
      />

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        kicker={editing?.id ? t('common.edit') : t('equipments.new')}
        title={t('equipments.modalTitle')}
        size="md"
      >
        {editing && <EquipmentForm initial={editing} categories={categories} onSubmit={save} onCancel={() => setEditing(null)} />}
      </Modal>

      {isAdmin && (
        <Modal
          open={managingCats}
          onClose={() => setManagingCats(false)}
          kicker={t('nav.equipments')}
          title={t('equipments.categoriesModalTitle')}
          size="sm"
        >
          {managingCats && <CategoriesManager categories={categories} onChanged={() => { loadCategories(); load(); }} />}
        </Modal>
      )}
    </>
  );
}
